import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { tz } from 'moment-timezone';
import DisplayArea from './DisplayArea';
import PaginationLinks from './PaginationLinks';
import SearchForm from './SearchForm';

import MessageBox from './MessageBox';
import SortFilters from './SortFilters';
import MediaFilters from './MediaFilters';
import getParam from '../helpers/helpers';
import './App.css';

class App extends Component {

  constructor() {
    super();

    this.respondToNewHash = this.respondToNewHash.bind(this);
    this.updateQuery = this.updateHash.bind(this, 'q');
    this.updateMediaType = this.updateHash.bind(this, 'section');
    this.updateSortOrder = this.updateHash.bind(this, 'sortOrder');
    this.updatePage = this.updateHash.bind(this, 'p');
    this.updateSite = this.updateHash.bind(this, 'site');

    // arbitrary number of items per page
    this.itemsPerPage = 10;

    this.state = {
      page: 1,
      searchTerm: '',
      mediaType: 'all',
      sortOrder: 'date',
      site: '',
      data: [],
      loading: false,
      resultCount: 0,
      error: false
    };
  }

  componentDidMount() {
    window.addEventListener('hashchange', this.respondToNewHash);
    this.respondToNewHash();

    // store approximation of user's timezone in sessionStorage
    window.sessionStorage.setItem('cbc-search-timezone', tz.guess());
  }

  // routing functions:

  // returns object with corresponding search parameters from window.location.hash
  parseHashParams() {
    const searchParameters = {
      page: 1,
      searchTerm: `${getParam('q').replace(/('|%27')/, '')}`,
      mediaType: 'all',
      sortOrder: 'relevance',
      site: `${getParam('site').replace('\\', '')}`
    };

    if (window.location.hash) {
      // Remove apostrophe (Layer7 will return a 400)
      const safeHash = window.location.hash.replace(/('|%27')/, '');
      const params = safeHash.substr(1).split('&');

      for (const param of params) {
        if (/^p=\d+$/.test(param)) {
          // if param starts with 'p=' followed by a number
          searchParameters.page = parseInt(param.substr(2), 10);
        } else if (/^q=[\w\s+"-*.%]+$/.test(param)) {
          // if param starts with 'q=' followed by letters, numbers, or common search operators
          searchParameters.searchTerm = decodeURI(param.substr(2));
        } else if (/^section=(sports|news|all)$/i.test(param)) {
          // if param starts with 'section=' followed by 'sports', 'news', 'all'
          searchParameters.mediaType = param.substr(8);
        } else if (/^sortOrder=(relevance|date)$/i.test(param)) {
          // if param is 'sortOrder=relevance||date'
          searchParameters.sortOrder = param.substr(10);
        } else if (/^site=[\w\s+"-*.%]+$/i.test(param)) {
          // if param starts with 'site=' followed by letters, numbers, or common search operators
          searchParameters.site = decodeURI(param.substr(5));
        }
      }
    }
    return searchParameters;
  }

  // components call this function, which triggers hashchange event, which triggers rerender
  updateHash(param, val) {
    const { searchTerm, mediaType, sortOrder, site } = this.state;
    const qVal = (param === 'q') ? val : searchTerm;
    const pVal = (param === 'p') ? val : 1;
    const typeVal = (param === 'section') ? val : mediaType;
    const sortVal = (param === 'sortOrder') ? val : sortOrder;
    const siteVal = (param === 'site') ? val : site;
    const newHash = `q=${qVal}&p=${pVal}&section=${typeVal}&sortOrder=${sortVal}&site=${siteVal}`;

    window.location.hash = newHash;
  }

  respondToNewHash() {
    const searchParams = this.parseHashParams();
    const storageData = window.sessionStorage.getItem(JSON.stringify(searchParams));
    const storageDataObj = storageData ? JSON.parse(storageData) : null;

    // if search params exist in sessionStorage and is < 1 min old, render that data
    if (storageData && (new Date().getTime() - storageDataObj.time < 60000)) {
      const { data, resultCount } = storageDataObj;
      this.setState({ loading: false, data, resultCount, ...searchParams });

    // otherwise, call api if there is a searchTerm
    } else if (searchParams.searchTerm) {
      this.search(searchParams);

    // otherwise, render blank default page
    } else {
      this.setState({
        loading: false,
        data: [],
        page: 1,
        searchTerm: '',
        mediaType: `${getParam('section') || 'all'}`,
        sortOrder: 'relevance',
        site: '',
        resultCount: 0,
        error: false
      });
    }
  }

  generateSearchUrl(params) {
    const { site, page, searchTerm, mediaType, sortOrder } = params;
    const apiUrl = this.props.apiUrl;

    let searchUrl = `${apiUrl}?q=${searchTerm}&pageSize=${this.itemsPerPage}&page=${page}`;

    if (sortOrder !== 'relevance') {
      searchUrl += `&sortOrder=${sortOrder}`;
    }

    if (mediaType !== 'all') {
      searchUrl += `&section=${mediaType}`;
    }

    if (site !== '') {
      searchUrl += `&site=${site}`;
    }
 
    return searchUrl;
  }

  search(params) {
    const searchUrl = this.generateSearchUrl(params);
    let resultCount = 0;
    // set loading state until API call is complete
    this.setState({ loading: true, data: [], ...params, error: false });

    fetch(searchUrl)
      .then(res => {
        resultCount = parseInt(res.headers.get('x-total-search-result-count'), 10);
        document.title = "Page " + this.state.page + " | Results for " + this.state.searchTerm + " | Search CBC.ca";
        return res.json();
      })
      .then(json => {
        const data = json;
        this.setState({ data, resultCount, loading: false });

        // store state data in sessionStorage
        window.sessionStorage.setItem(
          JSON.stringify({ ...params }),
          JSON.stringify({ data, resultCount, time: new Date().getTime() })
        );
      }).catch(err => {
        this.setState({loading: false, error: true});
        console.error(err)
      });
  }

  render() {
    const { data, loading, mediaType, page, resultCount, searchTerm, sortOrder, error } = this.state;
    const firstItemIndex = ((page - 1) * this.itemsPerPage) + 1;
    const lastItemIndex = (page * this.itemsPerPage > resultCount) ? resultCount : page * this.itemsPerPage;
    const displayAreaProps = { data, loading, mediaType, resultCount, searchTerm, sortOrder };

    // displaying entire message box
    const displayedItems = `${firstItemIndex} - ${lastItemIndex}`;

    const messageDisplay = searchTerm && !loading;
    const msgBoxProps = { displayedItems, mediaType, resultCount, searchTerm, error };
    const showSortFilters = Boolean(resultCount);

    return (
      <main className="searchPage">
        <h1 className="sr-only">Search CBC.ca</h1>
        <h1 className="embed-only">Search Results</h1>
        <MediaFilters updateMediaType={this.updateMediaType} mediaType={mediaType} />
        <section className="queryBox">
          <SearchForm
            updateQuery={this.updateQuery}
            searchTerm={searchTerm}
          />
          {!searchTerm && <span aria-hidden="true" className="initial-message">Please submit a search query to view search results from CBC.ca</span>}
          {messageDisplay && <MessageBox {...msgBoxProps} />}
          {messageDisplay &&
            <div className="filters">
              {showSortFilters && <SortFilters updateSortOrder={this.updateSortOrder} sortOrder={sortOrder} />}
            </div>}
        </section>
        <DisplayArea
          {...displayAreaProps}
        />
        {/* temporary hack until someone fixes the intermittent
          disappearance of x-total-search-result-count */}
        {resultCount !== 0 &&
          <section className="queryBox bottom">
          <PaginationLinks
            updatePage={this.updatePage}
            page={page}
            pageCount={Math.ceil(resultCount / this.itemsPerPage)}
          />
          <p></p>
          <SearchForm
            updateQuery={this.updateQuery}
            searchTerm={searchTerm}
          />
          </section>
        }
      </main>
    );
  }
}

App.propTypes = {
  apiUrl: PropTypes.string.isRequired
};

export default App;



// WEBPACK FOOTER //
// ./src/components/App.js